import { ClerkProvider as BaseClerkProvider } from '@clerk/clerk-react';
import { loadClerkJsScript } from '@clerk/shared';
import { loadWithRetry, match } from '@frontend/duck-tape';
import { addSentryBreadcrumb, captureSentryMessage } from '@frontend/web-utils';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

const CLERK_LOAD_TIMEOUT_MS = 20_000;
const CLERK_TOTAL_ATTEMPTS = 1;

export type SSOLoadState =
  | { onReload: () => void; status: 'failure' }
  | { onReload?: undefined; status: 'loading' }
  | { onReload?: undefined; status: 'success' };

const SSOLoadStateContext = createContext<MaybeUndefined<SSOLoadState>>(undefined);

export const useSSOLoadState = (): SSOLoadState => {
  const state = useContext(SSOLoadStateContext);
  if (!state) {
    throw new Error('useSSOLoadState must be used within an SSOProvider');
  }
  return state;
};

type LoadResult = 'failure' | 'loading' | 'success';

type SSOProviderProps = {
  afterSignInUrl?: string;
  afterSignUpUrl?: string;
  children: ReactNode;
  publishableKey: string;
  signInForceRedirectUrl?: string;
  signInUrl?: string;
  signUpForceRedirectUrl?: string;
  signUpUrl?: string;
};

export const SSOProvider = ({
  afterSignInUrl,
  afterSignUpUrl,
  children,
  publishableKey,
  signInForceRedirectUrl,
  signInUrl,
  signUpForceRedirectUrl,
  signUpUrl,
}: SSOProviderProps) => {
  const [loadResult, setLoadResult] = useState<LoadResult>(publishableKey ? 'loading' : 'success');

  useEffect(() => {
    if (!publishableKey) return;
    setLoadResult('loading');
    const controller = new AbortController();

    loadWithRetry({
      load: () => loadClerkJsScript({ publishableKey }),
      onFailedAttempt: ({ attemptNumber, error }) => {
        addSentryBreadcrumb({
          category: 'third_party.load',
          level: 'warning',
          message: `clerk load attempt ${attemptNumber} failed (${error.message})`,
        });
      },
      onSuccess: ({ attemptNumber }) => {
        addSentryBreadcrumb({
          category: 'third_party.load',
          level: 'info',
          message: `clerk loaded on attempt ${attemptNumber}`,
        });
      },
      retryDelayMs: 0,
      signal: controller.signal,
      timeoutMs: CLERK_LOAD_TIMEOUT_MS,
      totalAttempts: CLERK_TOTAL_ATTEMPTS,
    })
      .then(() => {
        if (controller.signal.aborted) return;
        setLoadResult('success');
      })
      .catch((error: Error) => {
        if (controller.signal.aborted) return;
        captureSentryMessage(`Failed to load clerk (${error.message})`, 'error');
        setLoadResult('failure');
      });

    return () => controller.abort();
  }, [publishableKey]);

  const onReload = useCallback(() => {
    window.location.reload();
  }, []);

  const loadState: SSOLoadState = match(loadResult)
    .with('loading', () => ({ status: 'loading' as const }))
    .with('failure', () => ({ onReload, status: 'failure' as const }))
    .with('success', () => ({ status: 'success' as const }))
    .exhaustive();

  const tree = publishableKey ? (
    <BaseClerkProvider
      publishableKey={publishableKey}
      signInFallbackRedirectUrl={afterSignInUrl || '/app/home'}
      signInForceRedirectUrl={signInForceRedirectUrl || '/app/home'}
      signInUrl={signInUrl || '/login'}
      signUpFallbackRedirectUrl={afterSignUpUrl || '/app/home'}
      signUpForceRedirectUrl={signUpForceRedirectUrl || '/app/home'}
      signUpUrl={signUpUrl || '/signup'}
    >
      {children}
      <div data-cl-theme="light" id="clerk-captcha" />
    </BaseClerkProvider>
  ) : (
    children
  );

  return <SSOLoadStateContext.Provider value={loadState}>{tree}</SSOLoadStateContext.Provider>;
};
